import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { useAuth } from '../../lib/auth';
import { Button } from '../ui/Button';
import { Container } from '../ui/Container';

interface NavItem {
  to: string;
  label: string;
  end: boolean;
}

const publicNavItems: NavItem[] = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About', end: false },
  { to: '/documents', label: 'Documents', end: false },
  { to: '/gallery', label: 'Gallery', end: false },
  { to: '/contact', label: 'Contact', end: false },
];

const ownerNavExtras: NavItem[] = [{ to: '/board', label: 'The Board', end: false }];

export function Header(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated } = useAuth();
  const ctaTo = isAuthenticated ? '/portal' : '/login';
  const ctaLabel = 'Owner Login';
  const navItems = isAuthenticated ? [...publicNavItems, ...ownerNavExtras] : publicNavItems;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <header
      className={`sticky top-0 z-50 bg-brand-white/95 backdrop-blur transition-shadow duration-200 ${
        scrolled ? 'shadow-header' : ''
      }`}
    >
      <Container as="div" className="flex h-20 items-center justify-between">
        <Link to="/" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <img src="/logo.webp" alt="" className="h-12 w-12 shrink-0" />
          <span className="flex flex-col leading-tight">
            <span className="text-base font-bold tracking-heading-tight text-brand-charcoal">
              Sunset Harbor
            </span>
            <span className="text-xs uppercase tracking-[0.18em] text-brand-body-medium">
              Condominiums
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `rounded px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-brand-charcoal'
                    : 'text-brand-body-medium hover:text-brand-charcoal'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Button to={ctaTo} variant="primary" size="md" className="ml-3">
            {ctaLabel}
          </Button>
        </nav>

        <button
          type="button"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-12 w-12 items-center justify-center rounded-md text-brand-charcoal hover:bg-brand-surface lg:hidden"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </Container>

      {open && (
        <div className="lg:hidden">
          <Container as="div" className="border-t border-brand-surface py-6">
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-3 text-base font-medium transition-colors ${
                      isActive
                        ? 'bg-brand-surface text-brand-charcoal'
                        : 'text-brand-body-dark hover:bg-brand-surface'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Button
                to={ctaTo}
                variant="primary"
                size="lg"
                className="mt-4 w-full"
                onClick={() => setOpen(false)}
              >
                {ctaLabel}
              </Button>
            </nav>
          </Container>
        </div>
      )}
    </header>
  );
}
